import { Box, Button, ButtonBase, Checkbox, Dialog, Divider, MenuItem, Select, SelectChangeEvent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, Toolbar, Tooltip, Typography } from "@mui/material"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import qualityCheckApi, { GlossaryItem, QualityCheckMyGlossaryImportMutationArgs } from "store/apis/qualityCheckApi"
import { qualityCheckMyGlossary } from "types/models/QualityCheckMyGlossary"
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import axios from "axios"
import { fileDownload } from "shared/utils"
import { isEqual } from "lodash";
import { RootState } from "store/store";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from 'react-i18next';

// Unified type for glossary items (supports both create and edit modes)
type UnifiedGlossaryItem = {
    glossaryId?: number;  // Only present in edit mode
    en: string;
    zh: string;
}

type GlossaryPanelProps = {
    isOpen: boolean;
    onClickClose: () => void;
    mode: 'create' | 'edit';
    // Create mode props
    glossaryItems?: GlossaryItem[];
    expandMyGlossary?: () => void;
    fileName?: string;
    isBuildGlossary?: boolean;
    // Edit mode props
    existingGlossary?: qualityCheckMyGlossary;
}

// Memoized row component
type GlossaryRowProps = {
    index: number;
    actualIndex: number;
    glossaryItem: UnifiedGlossaryItem;
    isEditable: boolean;
    isSelected: boolean;
    onChangeGlossary: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, lang: string, index: number) => void;
    onClickCheckBox: (index: number) => void;
};

const GlossaryRow = React.memo(
    ({ index, actualIndex, glossaryItem, isEditable, isSelected, onChangeGlossary, onClickCheckBox }: GlossaryRowProps) => {
        return (
            <TableRow key={actualIndex}>
                <TableCell sx={{ padding: "0px 16px" }}>
                    <Typography sx={{ fontSize: "0.875rem", textAlign: "center" }}>{actualIndex + 1}</Typography>
                </TableCell>
                <TableCell sx={{ padding: "0px 16px" }}>
                    <TextField
                        value={glossaryItem.en}
                        fullWidth
                        onChange={(e) => onChangeGlossary(e, "en", actualIndex)}
                        disabled={!isEditable}
                        sx={{
                            "& .MuiInputBase-input.Mui-disabled": {
                                WebkitTextFillColor: "black",
                            },
                        }}
                        InputProps={{ style: { fontSize: "0.875rem" }, disableUnderline: !isEditable }}
                    />
                </TableCell>
                <TableCell sx={{ padding: "0px 16px" }}>
                    <TextField
                        value={glossaryItem.zh}
                        fullWidth
                        onChange={(e) => onChangeGlossary(e, "zh", actualIndex)}
                        disabled={!isEditable}
                        sx={{
                            "& .MuiInputBase-input.Mui-disabled": {
                                WebkitTextFillColor: "black",
                            },
                        }}
                        InputProps={{ style: { fontSize: "0.875rem" }, disableUnderline: !isEditable }}
                    />
                </TableCell>
                <TableCell sx={{ padding: "0px 16px", width: "56px" }}>
                    {isEditable && (
                        <Checkbox
                            checked={isSelected}
                            onClick={() => onClickCheckBox(actualIndex)}
                            sx={{ padding: "0px" }}
                        />
                    )}
                </TableCell>
            </TableRow>
        );
    },
    (prevProps, nextProps) => {
        return (
            prevProps.glossaryItem.en === nextProps.glossaryItem.en &&
            prevProps.glossaryItem.zh === nextProps.glossaryItem.zh &&
            prevProps.isEditable === nextProps.isEditable &&
            prevProps.isSelected === nextProps.isSelected &&
            prevProps.actualIndex === nextProps.actualIndex
        );
    }
);

const ROWS_PER_PAGE = 50;

const GlossaryPanel = (props: GlossaryPanelProps) => {
    const { t } = useTranslation();
    const { mode, isOpen, onClickClose } = props;

    const [isEditable, setIsEditable] = useState(false);
    const [glossaryName, setGlossaryName] = useState("");
    const [glossaryItems, setGlossaryItems] = useState<UnifiedGlossaryItem[]>([]);
    const [myGlossaryId, setMyGlossaryId] = useState<number>(-1);
    const [selectedGlossary, setSelectedGlossary] = useState<number[]>([]);
    const [page, setPage] = useState(0);
    const [sortBy, setSortBy] = useState<'id' | 'en' | 'zh'>('id');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [sortedIndices, setSortedIndices] = useState<number[]>([]);
    const tableContainerRef = useRef<HTMLDivElement>(null);

    // Edit mode only: track modifications for diff
    const [modifiedGlossaryList, setModifiedGlossaryList] = useState<number[]>([]);
    const [deletedGlossaryList, setDeletedGlossaryList] = useState<number[]>([]);

    // API hooks
    const [createMyGlossary] = qualityCheckApi.useQualityCheckMyGlossaarImportMutation();
    const [modifyMyGlossary] = qualityCheckApi.useQualityCheckMyGlossaryGlossaryModifyMutation();

    const isLoggedIn = useSelector((state: RootState) => state.auth.isLoggedIn);

    // Pagination calculations
    const totalPages = useMemo(() =>
        Math.ceil(glossaryItems.length / ROWS_PER_PAGE),
        [glossaryItems.length]
    );

    // Function to calculate sorted indices
    const calculateSortedIndices = useCallback((items: UnifiedGlossaryItem[], by: 'id' | 'en' | 'zh', direction: 'asc' | 'desc') => {
        const indices = items.map((_, index) => index);
        return indices.sort((a, b) => {
            if (by === 'id') {
                return direction === 'asc' ? a - b : b - a;
            }
            const aVal = items[a][by].toLowerCase();
            const bVal = items[b][by].toLowerCase();
            if (aVal < bVal) return direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, []);

    // Get paginated items based on stored sorted indices
    const paginatedItemsWithIndex = useMemo(() => {
        // Use stored sorted indices, or default order if empty
        const indices = sortedIndices.length === glossaryItems.length 
            ? sortedIndices 
            : glossaryItems.map((_, i) => i);
        
        return indices
            .slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE)
            .map(originalIndex => ({
                item: glossaryItems[originalIndex],
                originalIndex
            }));
    }, [sortedIndices, glossaryItems, page]);

    // Reset scroll position when page changes
    useEffect(() => {
        if (tableContainerRef.current) {
            tableContainerRef.current.scrollTop = 0;
        }
    }, [page]);

    // Initialize data based on mode
    useEffect(() => {
        let newItems: UnifiedGlossaryItem[] = [];
        if (mode === 'create') {
            setGlossaryName(props.fileName ?? "");
            newItems = props.glossaryItems?.map(item => ({ en: item.en, zh: item.zh })) ?? [];
            setGlossaryItems(newItems);
            setMyGlossaryId(-1);
        } else if (mode === 'edit' && props.existingGlossary) {
            setGlossaryName(props.existingGlossary.glossaryName);
            newItems = props.existingGlossary.glossaryItems.map(item => ({
                glossaryId: item.glossaryId,
                en: item.en,
                zh: item.zh
            }));
            setGlossaryItems(newItems);
            setMyGlossaryId(props.existingGlossary.myGlossaryId);
        }
        setPage(0);
        setSortBy('id');
        setSortDirection('asc');
        // Initialize sorted indices with default order (by ID)
        setSortedIndices(newItems.map((_, i) => i));
        setModifiedGlossaryList([]);
        setDeletedGlossaryList([]);
    }, [mode, props.glossaryItems, props.fileName, props.existingGlossary]);

    // Reset editable state when dialog closes
    useEffect(() => {
        if (!isOpen) {
            setIsEditable(false);
        }
    }, [isOpen]);

    const handleDelete = useCallback(() => {
        // In edit mode, track deleted items that have glossaryId
        if (mode === 'edit') {
            const deletedIds = glossaryItems
                .filter((item, index) => selectedGlossary.includes(index) && item.glossaryId !== undefined && item.glossaryId !== -1)
                .map(item => item.glossaryId as number);
            setDeletedGlossaryList(prev => [...prev, ...deletedIds]);
        }

        // Update sortedIndices: remove deleted and adjust remaining
        setSortedIndices(prev => {
            const filtered = prev.filter(idx => !selectedGlossary.includes(idx));
            // Adjust indices: decrement for each deleted index that was lower
            return filtered.map(idx => {
                const decrementBy = selectedGlossary.filter(deleted => deleted < idx).length;
                return idx - decrementBy;
            });
        });

        setGlossaryItems(prev => prev.filter((_, index) => !selectedGlossary.includes(index)));
        setSelectedGlossary([]);
    }, [mode, glossaryItems, selectedGlossary]);

    const onClickCheckBox = useCallback((index: number) => {
        setSelectedGlossary(prev =>
            prev.includes(index)
                ? prev.filter((selected) => selected !== index)
                : [...prev, index]
        );
    }, []);

    const onClosePanel = useCallback(() => {
        onClickClose();
        setGlossaryName("");
        setGlossaryItems([]);
        setMyGlossaryId(-1);
        setPage(0);
        setSelectedGlossary([]);
        setModifiedGlossaryList([]);
        setDeletedGlossaryList([]);
        setSortBy('id');
        setSortDirection('asc');
        setSortedIndices([]);
    }, [onClickClose]);

    const handleSave = async () => {
        // Validate: check for empty items
        const emptyIndices = glossaryItems
            .map((item, index) => (item.en === "" || item.zh === "") ? index + 1 : -1)
            .filter(index => index !== -1);

        if (emptyIndices.length > 0) {
            toast.error(t('glossary.rowEmpty', { indices: emptyIndices.join(', ') }));
            return;
        }

        if (mode === 'create') {
            // Create mode: send entire array
            const payload: QualityCheckMyGlossaryImportMutationArgs = {
                glossaryName,
                glossaryItems: glossaryItems.map(item => ({ en: item.en, zh: item.zh }))
            };

            await createMyGlossary(payload).unwrap().then(() => {
                props.expandMyGlossary?.();
                onClickClose();
                toast.success(t('glossary.itemsAdded', { count: glossaryItems.length, name: glossaryName }));
            }).catch((error) => {
                toast.error(error.data?.message || 'Error creating glossary');
            });
        } else {
            // Edit mode: send diff (added, modified, deleted)
            const originalGlossary = props.existingGlossary;

            if (isEqual(
                { glossaryName, glossaryItems },
                { glossaryName: originalGlossary?.glossaryName, glossaryItems: originalGlossary?.glossaryItems }
            )) {
                toast.info(t('glossary.noModification'));
                return;
            }

            const addedGlossary = glossaryItems
                .filter(item => item.glossaryId === undefined || item.glossaryId === -1)
                .map(item => ({ en: item.en, zh: item.zh }));

            const modifiedGlossary = glossaryItems
                .filter((item, index) =>
                    modifiedGlossaryList.includes(index) &&
                    item.glossaryId !== undefined &&
                    item.glossaryId !== -1
                )
                .map(item => ({
                    glossaryId: item.glossaryId as number,
                    en: item.en,
                    zh: item.zh
                }));

            await modifyMyGlossary({
                myGlossaryId,
                editedName: glossaryName,
                editedGlossary: modifiedGlossary,
                addedGlossary,
                deletedGlossary: deletedGlossaryList
            }).unwrap().then(() => {
                if (addedGlossary.length > 0) {
                    toast.success(t('glossary.added', { count: addedGlossary.length, name: glossaryName }));
                }
                if (modifiedGlossary.length > 0) {
                    toast.success(t('glossary.modified', { count: modifiedGlossary.length, name: glossaryName }));
                }
                if (deletedGlossaryList.length > 0) {
                    toast.success(t('glossary.deleted', { count: deletedGlossaryList.length, name: glossaryName }));
                }
                setModifiedGlossaryList([]);
                setDeletedGlossaryList([]);
                setIsEditable(false);
                setSelectedGlossary([]);
            }).catch((error) => {
                toast.error(error.data?.message || 'Error modifying glossary');
            });
        }
    };

    const handleExportGlossary = async () => {
        const fileName = glossaryName + ".xlsx";
        const resultFile = await axios.post(
            `quality-check/extract-glossary/export`,
            JSON.stringify(glossaryItems.map(item => ({ en: item.en, zh: item.zh }))),
            {
                responseType: 'blob',
                headers: { "Content-Type": "application/json" }
            }
        );
        fileDownload(resultFile.data, fileName);
    };

    const onChangeGlossary = useCallback((
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        lang: string,
        index: number
    ) => {
        const value = event.target.value;

        setGlossaryItems(prev => {
            const newItems = prev.map((item, i) => {
                if (i === index) {
                    return lang === "en"
                        ? { ...item, en: value }
                        : { ...item, zh: value };
                }
                return item;
            });
            return newItems;
        });

        // In edit mode, track modified items that have glossaryId
        if (mode === 'edit') {
            setGlossaryItems(prev => {
                const item = prev[index];
                if (item?.glossaryId !== undefined && item.glossaryId !== -1) {
                    setModifiedGlossaryList(prevMod => 
                        prevMod.includes(index) ? prevMod : [...prevMod, index]
                    );
                }
                return prev;
            });
        }
    }, [mode]);

    const handleAddNewItem = useCallback(() => {
        const newItem: UnifiedGlossaryItem = mode === 'edit'
            ? { glossaryId: -1, en: "", zh: "" }
            : { en: "", zh: "" };

        setGlossaryItems(prev => [...prev, newItem]);

        // Reset to default sort (by ID) so new item appears at the end
        setSortBy('id');
        setSortDirection('asc');
        // Update sortedIndices to include new item at the end
        setSortedIndices(prev => [...prev, glossaryItems.length]);

        // Go to last page when adding new item
        const newTotalPages = Math.ceil((glossaryItems.length + 1) / ROWS_PER_PAGE);
        setPage(newTotalPages - 1);
    }, [mode, glossaryItems.length]);

    const handlePrevPage = () => {
        setPage(prev => Math.max(0, prev - 1));
    };

    const handleNextPage = () => {
        setPage(prev => Math.min(totalPages - 1, prev + 1));
    };

    const handlePageChange = (event: SelectChangeEvent<number>) => {
        setPage(Number(event.target.value));
    };

    const handleSort = (column: 'id' | 'en' | 'zh') => {
        let newDirection: 'asc' | 'desc' = 'asc';
        if (sortBy === column) {
            // Toggle direction
            newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
            setSortDirection(newDirection);
        } else {
            setSortBy(column);
            newDirection = 'asc';
            setSortDirection('asc');
        }
        // Recalculate sorted indices
        setSortedIndices(calculateSortedIndices(glossaryItems, column, newDirection));
        setPage(0); // Reset to first page when sorting
    };

    // Don't render if no data
    if (glossaryItems.length === 0 && glossaryName === "") {
        return null;
    }

    const isBuildGlossary = props.isBuildGlossary ?? false;
    const showSaveButton = mode === 'edit' || !(isBuildGlossary && !isLoggedIn);
    const saveDisabled = mode === 'create'
        ? (glossaryName === "" || glossaryItems.length === 0 || !isLoggedIn)
        : false;

    return (
        <Dialog
            fullWidth
            open={isOpen}
            onBackdropClick={onClosePanel}
            PaperProps={{ sx: { borderRadius: "5px" } }}
            onClose={() => { setIsEditable(false); }}
        >
            <Toolbar sx={{ minHeight: "45px" }}>
                <TextField
                    fullWidth
                    label={t('glossary.name')}
                    inputProps={{ sx: { textAlign: "center", fontWeight: "500" } }}
                    onChange={(e) => setGlossaryName(e.target.value)}
                    required
                    value={glossaryName}
                    disabled={mode === 'edit' && !isEditable}
                    InputProps={{
                        disableUnderline: mode === 'edit' && !isEditable,
                    }}
                    sx={mode === 'edit' ? {
                        "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: "black",
                        },
                    } : undefined}
                />
                <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Tooltip title={t('glossary.modifyGlossary')}>
                        <ButtonBase onClick={() => { setIsEditable(!isEditable); setSelectedGlossary([]); }}>
                            <EditIcon />
                        </ButtonBase>
                    </Tooltip>
                    <Tooltip title={t('glossary.exportGlossary')}>
                        <ButtonBase onClick={handleExportGlossary} disabled={glossaryName === ""}>
                            <FileDownloadIcon />
                        </ButtonBase>
                    </Tooltip>
                    {isEditable && (
                        <Tooltip title={t('glossary.delete')}>
                            <ButtonBase onClick={handleDelete}>
                                <DeleteIcon />
                            </ButtonBase>
                        </Tooltip>
                    )}
                    {showSaveButton && (
                        <Tooltip title={t('glossary.save')}>
                            <ButtonBase
                                onClick={handleSave}
                                disabled={saveDisabled}
                            >
                                <SaveIcon />
                            </ButtonBase>
                        </Tooltip>
                    )}
                    <Tooltip title={t('glossary.close')}>
                        <ButtonBase onClick={onClosePanel}>
                            <CloseIcon />
                        </ButtonBase>
                    </Tooltip>
                </Box>
            </Toolbar>
            <TableContainer
                ref={tableContainerRef}
                sx={{
                    maxHeight: "500px",
                    "::-webkit-scrollbar": { width: "5px", transform: "rotate(180deg)" },
                    "::-webkit-scrollbar-thumb": { background: "black" }
                }}
            >
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow key="header-row">
                            <TableCell sx={{ paddingTop: "0px", width: "60px" }}>
                                <TableSortLabel
                                    active={sortBy === 'id'}
                                    direction={sortBy === 'id' ? sortDirection : 'asc'}
                                    onClick={() => handleSort('id')}
                                >
                                    #
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ paddingTop: "0px" }}>
                                <TableSortLabel
                                    active={sortBy === 'en'}
                                    direction={sortBy === 'en' ? sortDirection : 'asc'}
                                    onClick={() => handleSort('en')}
                                >
                                    {t('glossary.english')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ paddingTop: "0px" }}>
                                <TableSortLabel
                                    active={sortBy === 'zh'}
                                    direction={sortBy === 'zh' ? sortDirection : 'asc'}
                                    onClick={() => handleSort('zh')}
                                >
                                    {t('glossary.chinese')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell padding="checkbox" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedItemsWithIndex.map(({ item: glossaryItem, originalIndex }, index) => (
                            <GlossaryRow
                                key={originalIndex}
                                index={index}
                                actualIndex={originalIndex}
                                glossaryItem={glossaryItem}
                                isEditable={isEditable}
                                isSelected={selectedGlossary.includes(originalIndex)}
                                onChangeGlossary={onChangeGlossary}
                                onClickCheckBox={onClickCheckBox}
                            />
                        ))}
                        {isEditable && (
                            <TableRow>
                                <TableCell colSpan={4} sx={{ padding: "0px 16px" }}>
                                    <ButtonBase
                                        sx={{ fontWeight: "700", width: "-webkit-fill-available", height: "30px" }}
                                        onClick={handleAddNewItem}
                                    >
                                        {t('glossary.new')}
                                    </ButtonBase>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Divider />
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 16px" }}>
                <Button
                    onClick={handlePrevPage}
                    disabled={page === 0}
                    startIcon={<ChevronLeftIcon />}
                >
                    {t('glossary.prevPage')}
                </Button>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                    <Select
                        size="small"
                        variant="standard"
                        value={page}
                        onChange={handlePageChange}
                        disableUnderline
                        IconComponent={() => null}
                        renderValue={(value) => `${(value as number) + 1} / ${totalPages || 1}`}
                        MenuProps={{
                            PaperProps: {
                                sx: { maxHeight: 200 }
                            }
                        }}
                        sx={{ 
                            '& .MuiSelect-select': { 
                                padding: '4px 8px !important',
                                fontSize: '0.875rem',
                                color: 'text.secondary',
                                minHeight: 'unset !important',
                                lineHeight: '1.43',
                                cursor: 'pointer',
                                backgroundColor: 'rgba(0, 0, 0, 0.06)',
                                borderRadius: '4px',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                }
                            },
                        }}
                    >
                        {Array.from({ length: totalPages || 1 }, (_, i) => (
                            <MenuItem key={i} value={i} sx={{ fontSize: '0.875rem' }}>
                                {i + 1}
                            </MenuItem>
                        ))}
                    </Select>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: '1.43' }}>
                        ({glossaryItems.length} {t('glossary.items')})
                    </Typography>
                </Box>
                <Button
                    onClick={handleNextPage}
                    disabled={page >= totalPages - 1}
                    endIcon={<ChevronRightIcon />}
                >
                    {t('glossary.nextPage')}
                </Button>
            </Box>
        </Dialog>
    );
};

export default GlossaryPanel;

